import React, { useContext, useEffect, useState } from 'react'
import HelpButton from 'Components/reusable/IconButtons/HelpButton'
import { userManualAPI } from 'api/UserManualAPI'
import { IUserManualPage } from 'types/userManualInterfaces'
import HelpModal from './HelpModal'
import { useLocation, useParams } from 'react-router'
import { useLogin } from 'hooks'

const HelpButtonContext = React.createContext({})

const { Provider } = HelpButtonContext

/**
 * Help button provider.
 *
 * @param children
 * @returns Provider for showing help button in context.
 */
export const HelpButtonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [article, setArticle] = useState<IUserManualPage>()
  const location = useLocation()
  const params = useParams()
  const { loggedIn } = useLogin()

  useEffect(() => {
    const noLoginPages = [
      '/login',
      '/terms',
      '/privacy-policy',
      '/activate',
      '/forgot-password',
      '/change-password',
      '/logout',
      '/confirm',
    ]

    ;(async () => {
      if (noLoginPages.some(page => location.pathname.includes(page)) || !loggedIn) {
        setArticle(undefined)
      } else {
        let path = location.pathname
        if (params && Object.keys(params).length > 0) {
          Object.keys(params).forEach(key => {
            const value = params[key]
            if (value) {
              path = path.replace(new RegExp(value, 'g'), `:${key}`)
            }
          })
        }

        try {
          const data = await userManualAPI.getUserManualPageByUrl(path)
          if (data && data.id) {
            setArticle(data)
          } else {
            setArticle(undefined)
          }
        } catch {
          // Silently fail - don't show help button if article fetch fails
          setArticle(undefined)
        }
      }
    })()
  }, [location.pathname, loggedIn, params])

  return (
    <Provider value={{}}>
      {children}
      {!open && article && <HelpButton onChange={setOpen} />}
      {open && article && (
        <HelpModal
          open={open}
          onClose={() => setOpen(false)}
          maxWidth={'xl'}
          fullWidth={true}
          article={article}
          backgroundColor={'#fff'}
        />
      )}
    </Provider>
  )
}

/**
 * Hook for displaying help button.
 *
 * @returns Help button provider context variables and functions.
 */
export const useHelpButton = () => {
  return useContext(HelpButtonContext)
}
